import React from 'react';
import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';
import { useDispatch } from 'react-redux';
import { defineMessages, useIntl } from 'react-intl';

import {
  DashboardUserMessage,
  VenueMessageType,
} from '@classpass/protobuf-definitions/lib/web/models/partners/message';
import { CancelReservationRequest } from '@classpass/protobuf-definitions/lib/web/models/partners/reservation';

import { createErrorBanner, createInfoBanner } from 'components/Banner';
import { DropdownToggle, DropdownMenu, DropdownItem } from 'components/Dropdown2';
import { Button, ButtonGroup, Dropdown, Form, Textarea } from 'components/HookForm';
import InfoBanner, { BANNER_VARIANTS } from 'components/InfoBanner/InfoBanner';
import Message from 'components/Message';
import { closeModal } from 'components/Modal';
import When from 'components/When';
import { ICONS } from 'constants/icons';
import URLS from 'constants/urls';
import appMessages from 'features/App/App.messages';
import { bem } from 'helpers/class-name';
import { reportError } from 'helpers/error-logging';
import { httpClient } from 'helpers/http';
import Segment from 'helpers/segment';

import { fetchScheduleData } from './YieldManagement.saga';

import './ReservationCancellationModal.scss';

const { block, element } = bem('reservation-cancellation-modal');

const messages = defineMessages({
  confirmCancellation: {
    id: 'yield-management.reservation-cancellation-modal.confirm-cancellation',
    defaultMessage: 'Confirm cancellation',
  },
  reasonLabel: {
    id: 'yield-management.reservation-cancellation-modal.reason-label',
    defaultMessage: 'Cancellation reason',
  },
  reasonPlaceholder: {
    id: 'yield-management.reservation-cancellation-modal.reason-placeholder',
    defaultMessage: 'Select cancellation reason',
  },
  disclaimerMessage: {
    id: 'yield-management.reservation-cancellation-modal.disclaimer-message',
    defaultMessage:
      'This client will receive a refund for the credits used to book this class and you will not receive any payout for this canceled reservation.',
  },
  messageSubject: {
    id: 'yield-management.reservation-cancellation-modal.message-subject',
    defaultMessage:
      'Update regarding your {startDateTime, date, time}, {startDateTime, date, long-w-dow} session at {venueName}{venueSubtitle, select, undefined {} other { ({venueSubtitle})}}',
  },
  instructorUnwellNoShow: {
    id: 'yield-management.reservation-cancellation-modal.instructor-unwell-no-show',
    defaultMessage: 'Instructor unwell/no-show',
  },
  overbookedClass: {
    id: 'yield-management.reservation-cancellation-modal.overbooked-class',
    defaultMessage: 'Overbooked class',
  },
  scheduleIncorrect: {
    id: 'yield-management.reservation-cancellation-modal.schedule-incorrect',
    defaultMessage: 'Schedule incorrect',
  },
  venueTechnicalIssue: {
    id: 'yield-management.reservation-cancellation-modal.venue-technical-issue',
    defaultMessage: 'Venue/Technical issue',
  },
  weatherConditions: {
    id: 'yield-management.reservation-cancellation-modal.weather-conditions',
    defaultMessage: 'Weather conditions',
  },
  other: {
    id: 'yield-management.reservation-cancellation-modal.other',
    defaultMessage: 'Other',
  },
  poorUserExperience: {
    id: 'yield-management.reservation-cancellation-modal.poor-user-experience',
    defaultMessage: 'Poor user experience',
  },
  descriptionLabel: {
    id: 'yield-management.reservation-cancellation-modal.description-label',
    defaultMessage: 'Please describe cancellation reason',
  },
  descriptionPlaceholder: {
    id: 'yield-management.reservation-cancellation-modal.description-placeholder',
    defaultMessage: 'Share why this reservation is being canceled',
  },
  confirm: {
    id: 'yield-management.reservation-cancellation-modal.confirm',
    defaultMessage: 'Confirm',
  },
  cancel: {
    id: 'yield-management.reservation-cancellation-modal.cancel',
    defaultMessage: 'Cancel',
  },
  successMessage: {
    id: 'yield-management.reservation-cancellation-modal.success-message',
    defaultMessage: 'Reservation for {fullName} at {className} on {startDate, date, long-w-dow} has been canceled.',
  },
  errorMessage: {
    id: 'yield-management.reservation-cancellation-modal.error-message',
    defaultMessage: 'Something went wrong when canceling the reservation. Please try again.',
  },
});

const reasons = [
  {
    key: 'POST_INSTRUCTOR_UNWELL_NO_SHOW',
    message: messages.instructorUnwellNoShow,
  },
  {
    key: 'POST_OVERBOOKED_CLASS',
    message: messages.overbookedClass,
  },
  {
    key: 'POST_SCHEDULE_INCORRECT',
    message: messages.scheduleIncorrect,
  },
  {
    key: 'POST_VENUE_TECHNICAL_ISSUE',
    message: messages.venueTechnicalIssue,
  },
  {
    key: 'POST_WEATHER_CONDITIONS',
    message: messages.weatherConditions,
  },
  {
    key: 'POST_POOR_USER_EXPERIENCE',
    message: messages.poorUserExperience,
  },
  {
    key: 'POST_OTHER',
    message: messages.other,
  },
];

const FORM_NAME = 'reservation-cancellation-modal-form';

function ReservationCancellationModal({
  reservationId,
  fullName,
  className,
  scheduleId,
  startDate,
  startDatetime,
  venueId,
  venueName,
  venueSubtitle,
}) {
  const intl = useIntl();
  const dispatch = useDispatch();

  const closeAriaLabel = intl.formatMessage(appMessages.close);

  function onClose() {
    dispatch(closeModal());
  }

  function buildRequestBody(reason, description) {
    const dashboardUserMessage = DashboardUserMessage.create({
      internalMessage: description,
      messageType: VenueMessageType.CANCEL,
      messageReason: reason,
      messageContent: '',
      subject: intl.formatMessage(messages.messageSubject, {
        startDateTime: new Date(startDatetime),
        venueName,
        venueSubtitle: venueSubtitle || '',
      }),
    });

    const cancelReservationRequest = CancelReservationRequest.create({
      reservation_id: reservationId,
      message: dashboardUserMessage,
    });

    return cancelReservationRequest;
  }

  async function onSubmit({ reason, description }, { resolveSubmit, rejectSubmit }) {
    const url = URLS.venue.cancelReservation;
    const requestBody = buildRequestBody(reason.key, description);

    try {
      await httpClient.post(url, requestBody);
      Segment.event('Reservation Cancelled - Post', {
        reason: reason?.key,
        reservationId,
        scheduleId,
        venueId,
      });
      dispatch(fetchScheduleData(venueId, scheduleId, true));
      dispatch(
        createInfoBanner(messages.successMessage, {
          fullName,
          className,
          startDate: new Date(startDate),
        })
      );
      resolveSubmit();
      onClose();
    } catch (error) {
      reportError('Error canceling reservation', error);
      dispatch(createErrorBanner(messages.errorMessage));
      rejectSubmit();
      onClose();
    }
  }

  return (
    <Form className={block()} id={FORM_NAME} onSubmit={onSubmit}>
      {function FormRender({ formState, watch }) {
        const { isSubmitting, isDirty } = formState;
        const selectedReason = watch('reason');

        return (
          <>
            <div className={element('wrapper')}>
              <header className={element('header')}>
                <Message className={element('heading')} as="h2" message={messages.confirmCancellation} />
                <button className={element('close-button')} type="button" onClick={onClose}>
                  <Isvg src={ICONS.cross} aria-label={closeAriaLabel} />
                </button>
              </header>

              <section className={element('details')}>
                <p className={element('detail', { bold: true })}>
                  <span>{fullName}</span>
                </p>

                <p className={element('detail')}>
                  <span>{className}</span>
                </p>

                <p className={element('detail')}>
                  <Message format="long-w-dow" value={new Date(startDate)} />
                </p>
              </section>

              <section className={element('cancellation-reason')}>
                <Dropdown
                  className={element('dropdown')}
                  attachment="left"
                  name="reason"
                  disabled={isSubmitting}
                  label={messages.reasonLabel}
                  required>
                  {function DropdownRender({ onChange }) {
                    return (
                      <>
                        <DropdownToggle className={element('dropdown-toggle', { placeholder: !selectedReason })}>
                          <Message message={selectedReason?.message || messages.reasonPlaceholder} />
                        </DropdownToggle>

                        <DropdownMenu className={element('dropdown-menu')}>
                          {reasons.map(reason => {
                            const { key } = reason;

                            return (
                              <DropdownItem
                                key={key}
                                isSelected={reason.key === selectedReason?.key}
                                onClick={() => onChange(reason)}>
                                <Message message={reason.message} />
                              </DropdownItem>
                            );
                          })}
                        </DropdownMenu>
                      </>
                    );
                  }}
                </Dropdown>
              </section>

              <When predicate={selectedReason?.key === 'POST_OTHER'}>
                <section className={element('reason-description')}>
                  <Textarea
                    name="description"
                    disabled={isSubmitting}
                    heightInLines={2}
                    label={messages.descriptionLabel}
                    placeholder={messages.descriptionPlaceholder}
                    required={selectedReason?.key === 'POST_OTHER'}
                    showRequiredLabel
                  />
                </section>
              </When>

              <section className={element('disclaimer')}>
                <InfoBanner
                  subtitle={messages.disclaimerMessage}
                  hideCTA
                  variant={BANNER_VARIANTS.YELLOW}
                  icon={ICONS.warningYellow}
                />
              </section>
            </div>
            <ButtonGroup className={element('buttons')}>
              <Message
                className={element('button')}
                as={Button}
                styled="tertiary"
                message={messages.cancel}
                waiting={!isDirty || isSubmitting}
                onClick={onClose}
              />

              <Message
                className={element('button', { submitBtn: true })}
                as={Button}
                message={messages.confirm}
                type="submit"
                waiting={!isDirty || isSubmitting}
              />
            </ButtonGroup>
          </>
        );
      }}
    </Form>
  );
}

ReservationCancellationModal.propTypes = {
  reservationId: PropTypes.number,
  fullName: PropTypes.string,
  className: PropTypes.string,
  scheduleId: PropTypes.number,
  startDate: PropTypes.string,
  startDatetime: PropTypes.string,
  venueId: PropTypes.number,
  venueName: PropTypes.string,
  venueSubtitle: PropTypes.string,
};

export default ReservationCancellationModal;
